import { Component, inject, Input, OnInit } from '@angular/core'
import { Venue } from '@app/domain/Venue'
import { Organisation } from '@app/domain/Organisation'
import { Booking } from '@app/domain/Booking'
import { v4 as uuidv4 } from 'uuid'
import { Contact } from '@app/domain/Contact'
import { BookingSource } from '@app/domain/BookingSource'
import { BookingStatus, BookingStatusType } from '@app/domain/BookingStatus'
import { Business } from '@app/domain/Business'
import { BookingService } from '@services/booking.service'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Observable, finalize } from 'rxjs'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { DateTime } from 'luxon'

@Component({
    selector: 'app-rebook-booking',
    templateUrl: './rebook-booking.component.html',
})
export class RebookBookingComponent implements OnInit {

    @Input() organisation!: Organisation
    @Input() business!: Business
    @Input() venue!: Venue
    @Input() sourceBooking!: Booking
    form!: FormGroup
    isSubmitting = false
    dateIds: string[] = []
    private nextId = 0
    private bookingService = inject(BookingService)
    private modal = inject(NgbActiveModal)
    private fb = inject(FormBuilder)

    ngOnInit() {
        this.form = this.makeForm()
    }

    dismiss() {
        this.modal.dismiss()
    }

    get dates(): FormArray {
        return this.form.get('dates') as FormArray
    }

    get tableNames(): string {
        const area = this.venue.areaBookingIsIn(this.sourceBooking)
        if (!area) {
            return ''
        }
        const tables = area.tablesUsedForBooking(this.sourceBooking)
        return tables.map(table => table.formattedDisplayName).join(', ')
    }

    get reasonName(): string | null {
        if (!this.sourceBooking.reasonId) {
            return null
        }
        const reason = this.venue.reasonWithId(this.sourceBooking.reasonId)
        return reason ? reason.displayName : null
    }

    get formattedTime(): string {
        return DateTime
            .fromJSDate(this.sourceBooking.start)
            .setZone(this.venue.timeZone)
            .toFormat('HH:mm')
    }

    addDate() {
        this.dates.push(this.fb.control(null, [Validators.required]))
        this.dateIds.push(`date-${this.nextId++}`)
    }

    removeDate(index: number) {
        this.dates.removeAt(index)
        this.dateIds.splice(index, 1)
    }

    rebookBooking() {
        if (this.form.invalid || this.isSubmitting) {
            return
        }
        const dates = this.dates.value as string[]
        const bookings = this.makeBookings(dates)
        if (bookings.length === 0) {
            return
        }
        this.isSubmitting = true
        this.addBookings(this.business, this.venue, bookings)
            .pipe(
                finalize(() => this.isSubmitting = false)
            )
            .subscribe(() => {
                this.modal.close()
            })
    }

    private makeForm() {
        this.dateIds = [`date-${this.nextId++}`]
        return this.fb.group({
            dates: this.fb.array([
                this.fb.control(null, [Validators.required])
            ])
        })
    }

    private makeBookings(dates: string[]): Booking[] {
        const sourceStart = DateTime
            .fromJSDate(this.sourceBooking.start)
            .setZone(this.venue.timeZone)
        return dates
            .filter(date => date !== null)
            .map(dateString => {
                const selectedDate = DateTime.fromISO(dateString, { zone: this.venue.timeZone })
                const dateTime = selectedDate.set({
                    hour: sourceStart.hour,
                    minute: sourceStart.minute,
                    second: 0,
                    millisecond: 0
                }).toJSDate()
                return this.makeBooking(dateTime)
            })
    }

    private makeBooking(dateTime: Date): Booking {
        const initialStatus = this.makeInitialStatus()
        const booking = new Booking(
            uuidv4(),
            this.organisation.id,
            [...this.sourceBooking.tableIds],
            new Contact(
                this.sourceBooking.contact.firstName,
                this.sourceBooking.contact.lastName,
                this.sourceBooking.contact.emailAddress,
                this.sourceBooking.contact.phoneNumber
            ),
            this.sourceBooking.customer,
            this.sourceBooking.size,
            dateTime,
            this.sourceBooking.duration,
            this.sourceBooking.notes,
            this.sourceBooking.merchantNotes,
            this.sourceBooking.agreedToMarketing,
            this.sourceBooking.agreedToTerms,
            null,
            BookingSource.Merchant,
            [],
            [
                initialStatus,
            ],
            [],
            [],
            this.sourceBooking.reasonId,
            this.sourceBooking.eventId,
            [...this.sourceBooking.answers],
            null,
            null,
            true,
            this.sourceBooking.requiresWheelchairAccess,
            this.sourceBooking.requiresDogFriendly,
            this.sourceBooking.requiresPramAccessible,
            this.sourceBooking.requiresHighchairFriendly,
            this.sourceBooking.lockedToTables,
            null,
            null,
            null,
            null
        )
        return booking
    }

    private makeInitialStatus() {
        const metadata = new Map()
        let bookingStatusType = BookingStatusType.Booked
        return new BookingStatus(
            uuidv4(),
            metadata,
            new Date(),
            bookingStatusType
        )
    }

    private addBookings(business: Business, venue: Venue, bookings: Booking[]): Observable<Booking[]> {
        const idempotencyKey = uuidv4()
        return this.bookingService.addBookings(
            business.id,
            venue.id,
            bookings,
            idempotencyKey
        )
    }
}
